import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';

import { errorText, isStale } from '~/lib/apiError';

const STALE_MESSAGE =
  'This record was changed by someone else since you opened it. Reload the page to see the latest version, then make your edits again.';

/**
 * Runs a save (an RTK Query mutation's `.unwrap()`): `busy` while it runs, a success toast, and
 * on failure an `error` sentence for an `Alert` (a 409 stale conflict gets its own wording).
 * `submit` resolves true when the save went through, so the caller can `markSaved` or navigate.
 */
export function useRecordSave<D>({
  save,
  success,
  failure,
}: {
  save: (draft: D) => Promise<unknown>;
  /** Toast on success: "Persona updated". */
  success: string;
  /** Fallback when the API gives no sentence: "The persona could not be saved. Try again." */
  failure: string;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = useCallback(
    async (draft: D): Promise<boolean> => {
      setBusy(true);
      setError(null);
      try {
        await save(draft);
        toast.success(success);
        return true;
      } catch (err) {
        setError(isStale(err) ? STALE_MESSAGE : errorText(err, failure));
        return false;
      } finally {
        setBusy(false);
      }
    },
    [save, success, failure],
  );

  return { submit, busy, error, clearError: () => setError(null) };
}
